import { memo, useMemo } from 'react'
import { Stepper as MuiStepper, Step, StepLabel, SxProps } from '@mui/material'
import { useTranslations } from '@/core/translation'
import { useDeviceType, usePackageInfo } from '@/utils'
import { Box } from '@/components/Layout'
import { withErrorBoundary } from '@/components/Utils'
import { stepperIds } from './Stepper.data'
import { StepperProps } from './Stepper.types'
import { stepperStyles as styles } from './Stepper.style'

const Stepper = ({ activeStep = 0, steps, ...rest }: StepperProps) => {
  const t = useTranslations('steps')
  const { isMobile } = useDeviceType()
  const { currentPackage, modify } = usePackageInfo()

  const { defaultSteps, filteredActiveStep } = useMemo(() => {
    const isModify = modify === 'true'

    const allSteps = steps || [
      {
        label: t('eventTickets'),
        mobileLabel: t('eventTicketsMobile'),
        hide: (activeStep > 0 || isModify) && !currentPackage?.cartInfo?.ticket,
      },
      {
        label: t('stays'),
        mobileLabel: t('staysMobile'),
        hide: (activeStep > 1 || isModify) && !currentPackage?.cartInfo?.hotel,
      },
      {
        label: t('flights'),
        mobileLabel: t('flightsMobile'),
        hide: (activeStep > 2 || isModify) && !currentPackage?.cartInfo?.flight,
      },
      {
        label: t('reviewYourPackage'),
        mobileLabel: t('reviewYourPackageMobile'),
        hide: false,
      },
    ]

    let currentFilteredActiveStep = 0

    const filteredSteps = []

    for (let i = 0; i < allSteps.length; i++) {
      const step = allSteps[i]

      if (!step.hide) {
        // If this visible step comes before the activeStep
        if (i < activeStep) currentFilteredActiveStep++

        filteredSteps.push(step)
      }
    }

    return {
      defaultSteps: filteredSteps,
      filteredActiveStep: currentFilteredActiveStep,
    }
  }, [
    activeStep,
    currentPackage?.cartInfo?.flight,
    currentPackage?.cartInfo?.hotel,
    currentPackage?.cartInfo?.ticket,
    modify,
    steps,
    t,
  ])

  return (
    <Box id={stepperIds.root} component={'section'}>
      <MuiStepper
        activeStep={filteredActiveStep}
        sx={styles.stepper}
        alternativeLabel
        connector={null}
        {...rest}>
        {defaultSteps.map((step, index) => {
          const isCompletedOrActive = index <= filteredActiveStep

          return (
            <Step
              key={step.label}
              sx={[styles.step, isCompletedOrActive && styles.activeBorder] as SxProps}>
              <StepLabel sx={styles.stepLabel} icon={null}>
                {isMobile && step.mobileLabel ? step.mobileLabel : step.label}
              </StepLabel>
            </Step>
          )
        })}
      </MuiStepper>
    </Box>
  )
}

export default memo(withErrorBoundary(Stepper))
